import { useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useCollection } from "../../hooks/useCollection";
import ImageUploader from "../../components/admin/ImageUploader";

const emptyForm = {
  title: "",
  summary: "",
  body: "",
  imageUrl: "",
  link: "",
  order: 0,
};

export default function NewsAdmin() {
  const { items, loading } = useCollection("news", "order", "asc");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (field) => (e) =>
    setForm({ ...form, [field]: e.target.value });

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      summary: item.summary || "",
      body: item.body || "",
      imageUrl: item.imageUrl || "",
      link: item.link || "",
      order: item.order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, "news", editingId), payload);
      } else {
        await addDoc(collection(db, "news"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      resetForm();
    } catch (err) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`ลบ "${item.title}"?`)) return;
    try {
      await deleteDoc(doc(db, "news", item.id));
    } catch (err) {
      alert(`ลบไม่สำเร็จ: ${err.message}`);
    }
  };

  return (
    <div>
      <h2 className="mb-4">จัดการ News</h2>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {editingId ? "แก้ไขข่าว" : "เพิ่มข่าวใหม่"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">หัวข้อ</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.title}
                  onChange={handleChange("title")}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">ลำดับแสดงผล (น้อย = แรก)</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.order}
                  onChange={handleChange("order")}
                />
              </div>
              <div className="col-12">
                <label className="form-label">สรุปย่อ (แสดงบนการ์ด)</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.summary}
                  onChange={handleChange("summary")}
                />
              </div>
              <div className="col-12">
                <label className="form-label">เนื้อหาเต็ม (ถ้ามี)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={form.body}
                  onChange={handleChange("body")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">ลิงก์ "อ่านข่าวนี้"</label>
                <input
                  type="url"
                  className="form-control"
                  value={form.link}
                  onChange={handleChange("link")}
                  placeholder="https://..."
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">รูปภาพ</label>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(url) => setForm({ ...form, imageUrl: url })}
                  folder="news"
                />
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving}>
                {saving ? "กำลังบันทึก..." : editingId ? "บันทึกการแก้ไข" : "เพิ่มข่าว"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">รายการข่าว ({items.length})</h5>
          {loading ? (
            <div>กำลังโหลด...</div>
          ) : items.length === 0 ? (
            <div className="text-muted">ยังไม่มีข่าว — เพิ่มข่าวแรกได้เลย</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>รูป</th>
                    <th>หัวข้อ</th>
                    <th style={{ width: 80 }}>ลำดับ</th>
                    <th style={{ width: 180 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt=""
                            style={{
                              width: 60,
                              height: 45,
                              objectFit: "cover",
                            }}
                            className="rounded"
                          />
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">{item.title}</div>
                        <div className="small text-muted">{item.summary}</div>
                      </td>
                      <td>{item.order ?? 0}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(item)}
                        >
                          แก้ไข
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(item)}
                        >
                          ลบ
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
